import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import {
  getClockInContext,
  clockInWithToken,
  clockOutWithToken,
  clockInWithSelfie,
  clockOutWithSelfie,
  pingClockLocation,
} from "@/utils/clock-token.functions";

import { submitAbsenceWithToken, listMyAbsencesWithToken } from "@/utils/absence.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  CalendarX,
  Camera,
  Clock,
  History,
  Loader2,
  MapPin,
  Paperclip,
  Play,
  Square,
} from "lucide-react";
import { GEOFENCE_RADIUS_METERS, getCurrentPosition } from "@/lib/geolocation";
import { gpsBlockedMessageKey } from "@/lib/gps-help";
import { PwaInstallBanner } from "@/components/clock/PwaInstallBanner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ClockHelpButton } from "@/components/clock/ClockHelpButton";
import { AddToHomeScreenButton } from "@/components/clock/AddToHomeScreenButton";
import { EnablePushButton } from "@/components/clock/EnablePushButton";
import { SelfieCaptureDialog } from "@/components/clock/SelfieCaptureDialog";
import { ClockMissingAlertsBanner } from "@/components/clock/ClockMissingAlertsBanner";
import i18n from "@/i18n";
import { buildClockPath } from "@/lib/clock-url";

const LAST_TOKEN_KEY = "clock-last-token";

export const Route = createFileRoute("/clock/$token")({
  component: ClockPage,
  head: ({ params }) => {
    const manifestHref = `/api/public/clock-manifest/${params.token}`;
    const canonicalHref = buildClockPath(params.token);
    return {
      meta: [
        { title: i18n.t("clock.title") },
        { name: "theme-color", content: "#0f172a" },
        { name: "apple-mobile-web-app-capable", content: "yes" },
        { name: "apple-mobile-web-app-status-bar-style", content: "default" },
        { name: "apple-mobile-web-app-title", content: i18n.t("clock.title") },
        { name: "mobile-web-app-capable", content: "yes" },
      ],
      links: [
        { rel: "canonical", href: canonicalHref },
        { rel: "manifest", href: manifestHref },
        { rel: "apple-touch-icon", href: "/favicon.png" },
      ],
    };
  },

  errorComponent: ({ error }) => (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>{i18n.t("clock.invalidLink")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : i18n.t("clock.invalidLinkBody")}
          </p>
        </CardContent>
      </Card>
    </div>
  ),
});

type Project = { id: string; code: string; name: string };
type JobSite = {
  id: string;
  name: string;
  project_id: string;
  latitude: number | null;
  longitude: number | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
};
type ActiveEntry = {
  id: string;
  clock_in: string;
  project_id: string | null;
  job_site_id: string | null;
  notes: string | null;
} | null;

type Ctx = {
  profile: { id: string; fullName: string | null; email: string };
  company: { id: string; name: string };
  projects: Project[];
  jobSites: JobSite[];
  activeEntry: ActiveEntry;
  gpsRadiusMeters: number;
};

type Absence = {
  id: string;
  absence_date: string;
  category: string;
  reason: string;
  status: "pending" | "approved" | "rejected";
  review_notes: string | null;
  created_at: string;
  attachment_path: string | null;
};

function ClockPage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const getCtx = useServerFn(getClockInContext);
  const clockInFn = useServerFn(clockInWithToken);
  const clockOutFn = useServerFn(clockOutWithToken);
  const clockInSelfieFn = useServerFn(clockInWithSelfie);
  const clockOutSelfieFn = useServerFn(clockOutWithSelfie);
  const submitAbsenceFn = useServerFn(submitAbsenceWithToken);
  const listAbsencesFn = useServerFn(listMyAbsencesWithToken);

  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [selProject, setSelProject] = useState<string>("");
  const [selJobSite, setSelJobSite] = useState<string>("");
  // Job site the employee is at when clocking OUT (may differ from clock-in).
  const [selOutJobSite, setSelOutJobSite] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [breakMin, setBreakMin] = useState<number>(0);
  const [busy, setBusy] = useState(false);

  // Selfie fallback when GPS is denied
  const [selfieDialog, setSelfieDialog] = useState<{
    open: boolean;
    kind: "in" | "out";
  }>({ open: false, kind: "in" });
  const [selfieBusy, setSelfieBusy] = useState(false);
  // Trava síncrona contra toque duplo: o estado (selfieBusy) só desabilita o
  // botão no próximo render — dois cliques rápidos disparariam 2 requisições.
  const selfieSubmittingRef = useRef(false);

  // Absence form
  const [absDate, setAbsDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [absCategory, setAbsCategory] = useState<"sick" | "personal" | "other">("sick");
  const [absReason, setAbsReason] = useState("");
  const [absFile, setAbsFile] = useState<File | null>(null);
  const [absSubmitting, setAbsSubmitting] = useState(false);

  // History
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Persist last valid token for PWA reopen
  useEffect(() => {
    if (typeof window !== "undefined" && token) {
      try {
        localStorage.setItem(LAST_TOKEN_KEY, token);
      } catch {
        /* ignore */
      }
    }
  }, [token]);

  // iPhone Safari can keep serving an old cached clock page for a long time.
  // The clock flow no longer uses a service worker at all; whenever this page
  // opens, aggressively remove old clock workers/caches and reload once.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    const isInIframe = (() => {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    })();
    const isPreviewHost =
      window.location.hostname.includes("id-preview--") ||
      window.location.hostname.includes("lovableproject.com");

    const CLEANUP_KEY = "clock-sw-cleanup-v4";
    const alreadyCleanedThisTab = (() => {
      try {
        return sessionStorage.getItem(CLEANUP_KEY) === "1";
      } catch {
        return false;
      }
    })();

    void (async () => {
      try {
        const [regs, keys] = await Promise.all([
          navigator.serviceWorker.getRegistrations(),
          "caches" in window ? caches.keys() : Promise.resolve([] as string[]),
        ]);

        const clockCacheKeys = keys.filter((k) => k.startsWith("clock-shell"));
        const hadOldClockCache = regs.length > 0 || clockCacheKeys.length > 0;

        await Promise.all([
          ...regs.map((r) => r.unregister()),
          ...clockCacheKeys.map((k) => caches.delete(k)),
        ]);

        if (isInIframe || isPreviewHost) return;

        if (!alreadyCleanedThisTab && hadOldClockCache) {
          try {
            sessionStorage.setItem(CLEANUP_KEY, "1");
          } catch {
            /* ignore */
          }
          window.location.reload();
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await getCtx({ data: { token } })) as Ctx;
      setCtx(data);
      if (data.activeEntry) {
        setSelProject(data.activeEntry.project_id ?? "");
        setSelJobSite(data.activeEntry.job_site_id ?? "");
        // Default the clock-out site to the clock-in site; the employee changes
        // it only if they moved to another site during the shift.
        setSelOutJobSite(data.activeEntry.job_site_id ?? "");
      }
    } catch (e) {
      const msg =
        e instanceof Response
          ? await e.text().catch(() => t("clock.invalidLink"))
          : e instanceof Error
            ? e.message
            : t("clock.invalidLink");
      setError(msg || t("clock.invalidLink"));
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = (await listAbsencesFn({ data: { token } })) as {
        absences: Absence[];
      };
      setAbsences(res.absences);
    } catch {
      /* silent */
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Background GPS pings: once the employee passes 8h of an open shift,
  // silently capture and send the current location every 5 minutes so the
  // 10h auto clock-out cron can record the most recent coordinates.
  const pingFn = useServerFn(pingClockLocation);
  useEffect(() => {
    if (!ctx?.activeEntry || typeof navigator === "undefined" || !navigator.geolocation) {
      return;
    }
    const clockInMs = new Date(ctx.activeEntry.clock_in).getTime();
    const send = () => {
      const elapsedH = (Date.now() - clockInMs) / 3_600_000;
      if (elapsedH < 8) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          pingFn({
            data: {
              token,
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy ?? null,
            },
          }).catch(() => undefined);
        },
        () => undefined,
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
      );
    };
    // Fire one immediately (will no-op if < 8h), then every 5 minutes.
    send();
    const id = window.setInterval(send, 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [ctx?.activeEntry, token, pingFn]);

  const filteredJobSites = useMemo(
    () =>
      ctx
        ? selProject
          ? ctx.jobSites.filter((s) => s.project_id === selProject)
          : ctx.jobSites
        : [],
    [ctx, selProject],
  );

  // Note: do NOT await navigator.permissions.query() before getCurrentPosition().
  // On iOS Safari, any await between the user tap and navigator.geolocation.*
  // breaks the user-activation gesture and causes the GPS prompt to silently
  // fail / time out. We diagnose permission state only AFTER an error.
  const wasGeolocationDenied = async (): Promise<boolean> => {
    if (typeof navigator === "undefined") return false;
    if (!("permissions" in navigator) || !navigator.permissions?.query) {
      return false;
    }
    try {
      const status = await navigator.permissions.query({
        name: "geolocation" as PermissionName,
      });
      return status.state === "denied";
    } catch {
      return false;
    }
  };

  const handleClockIn = async () => {
    if (!ctx) return;
    if (!selJobSite) {
      toast.error(t("clock.errors.selectJobSite"));
      return;
    }
    const projectId = ctx.jobSites.find((s) => s.id === selJobSite)?.project_id ?? "";
    if (!projectId) {
      toast.error(t("clock.errors.selectJobSite"));
      return;
    }
    // Kick off GPS IMMEDIATELY (synchronously inside the click handler) so iOS
    // keeps the user gesture alive and shows the permission prompt.
    const gpsPromise = getCurrentPosition();
    setBusy(true);
    try {
      const coords = await gpsPromise;
      await clockInFn({
        data: {
          token,
          projectId,
          jobSiteId: selJobSite,
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
          notes: notes.trim() || undefined,
        },
      });
      toast.success(t("clock.toasts.punchedIn"));
      setNotes("");
      await load();
    } catch (e) {
      const msg =
        e instanceof Response
          ? await e.text().catch(() => "Erro")
          : e instanceof Error
            ? e.message
            : "Erro";
      console.warn("[clock-in] GPS failed, falling back to selfie:", msg);
      const denied = await wasGeolocationDenied();
      const key = denied ? gpsBlockedMessageKey() : "clock.toasts.gpsFailed";
      toast.info(t(key), { duration: denied ? 12000 : 6000 });
      setSelfieDialog({ open: true, kind: "in" });
    } finally {
      setBusy(false);
    }
  };

  const handleClockOut = async () => {
    if (!ctx?.activeEntry) return;
    // Confirm if employee is clocking out before completing 8h of work.
    const clockInMs = new Date(ctx.activeEntry.clock_in).getTime();
    const elapsedH = (Date.now() - clockInMs) / 3_600_000;
    if (elapsedH < 8) {
      const hh = Math.floor(elapsedH);
      const mm = Math.floor((elapsedH - hh) * 60);
      const ok = window.confirm(
        `Você já bateu entrada e trabalhou apenas ${hh}h${String(mm).padStart(2, "0")} (menos de 8h).\n\nAgora você está batendo SAÍDA. Deseja confirmar?`,
      );
      if (!ok) return;
    }
    // Same iOS user-gesture rule: fire GPS first, diagnose later.
    const gpsPromise = getCurrentPosition();
    setBusy(true);
    try {
      const coords = await gpsPromise;
      await clockOutFn({
        data: {
          token,
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
          breakMinutes: breakMin,
          notes: notes.trim() || undefined,
          jobSiteId: selOutJobSite || undefined,
        },
      });
      toast.success(t("clock.toasts.punchedOut"));
      setBreakMin(0);
      setNotes("");
      await load();
    } catch (e) {
      const msg =
        e instanceof Response
          ? await e.text().catch(() => "Erro")
          : e instanceof Error
            ? e.message
            : "Erro";
      console.warn("[clock-out] GPS failed, falling back to selfie:", msg);
      const denied = await wasGeolocationDenied();
      const key = denied ? gpsBlockedMessageKey() : "clock.toasts.gpsFailedExit";
      toast.info(t(key), { duration: denied ? 12000 : 6000 });
      setSelfieDialog({ open: true, kind: "out" });
    } finally {
      setBusy(false);
    }
  };

  const handleSelfieCapture = async (data: {
    base64: string;
    mime: string;
    gps: { latitude: number; longitude: number; accuracy: number | null } | null;
  }) => {
    if (selfieSubmittingRef.current) return; // ignora toque duplo
    selfieSubmittingRef.current = true;
    setSelfieBusy(true);
    try {
      if (selfieDialog.kind === "in") {
        const projectId = ctx?.jobSites.find((s) => s.id === selJobSite)?.project_id ?? "";
        if (!selJobSite || !projectId) {
          toast.error(t("clock.errors.selectBeforeSelfie"));
          return;
        }
        await clockInSelfieFn({
          data: {
            token,
            projectId,
            jobSiteId: selJobSite,
            selfieBase64: data.base64,
            selfieMime: data.mime,
            latitude: data.gps?.latitude ?? null,
            longitude: data.gps?.longitude ?? null,
            accuracy: data.gps?.accuracy ?? null,
            notes: notes.trim() || undefined,
          },
        });
        toast.success(
          data.gps ? t("clock.toasts.entryWithPhotoGps") : t("clock.toasts.entryWithPhoto"),
        );
      } else {
        await clockOutSelfieFn({
          data: {
            token,
            selfieBase64: data.base64,
            selfieMime: data.mime,
            latitude: data.gps?.latitude ?? null,
            longitude: data.gps?.longitude ?? null,
            accuracy: data.gps?.accuracy ?? null,
            breakMinutes: breakMin,
            notes: notes.trim() || undefined,
            jobSiteId: selOutJobSite || undefined,
          },
        });
        toast.success(
          data.gps ? t("clock.toasts.exitWithPhotoGps") : t("clock.toasts.exitWithPhoto"),
        );
      }
      setSelfieDialog({ open: false, kind: selfieDialog.kind });
      setNotes("");
      setBreakMin(0);
      await load();
    } catch (e) {
      const msg =
        e instanceof Response
          ? await e.text().catch(() => "Erro")
          : e instanceof Error
            ? e.message
            : "Erro";
      toast.error(msg);
    } finally {
      selfieSubmittingRef.current = false;
      setSelfieBusy(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const b64 = result.split(",")[1] ?? "";
        resolve(b64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const handleSubmitAbsence = async () => {
    if (!absReason.trim() || absReason.trim().length < 3) {
      toast.error(t("clock.absence.describeShort"));
      return;
    }
    setAbsSubmitting(true);
    try {
      let attachment: {
        attachmentBase64?: string;
        attachmentMime?: string;
        attachmentName?: string;
      } = {};
      if (absFile) {
        if (absFile.size > 8 * 1024 * 1024) {
          toast.error(t("clock.absence.fileTooLarge"));
          setAbsSubmitting(false);
          return;
        }
        const b64 = await fileToBase64(absFile);
        attachment = {
          attachmentBase64: b64,
          attachmentMime: absFile.type,
          attachmentName: absFile.name,
        };
      }
      await submitAbsenceFn({
        data: {
          token,
          absenceDate: absDate,
          category: absCategory,
          reason: absReason.trim(),
          ...attachment,
        },
      });
      toast.success(t("clock.absence.submitted"));
      setAbsReason("");
      setAbsFile(null);
      await loadHistory();
    } catch (e) {
      const msg =
        e instanceof Response
          ? await e.text().catch(() => "Erro")
          : e instanceof Error
            ? e.message
            : "Erro";
      toast.error(msg);
    } finally {
      setAbsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !ctx) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>{t("clock.invalidLink")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{error ?? t("clock.invalidLinkBody")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const active = ctx.activeEntry;
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-background px-4 py-6">
      <div className="mx-auto max-w-md space-y-4">
        <PwaInstallBanner />

        <div className="relative text-center">
          <div className="absolute left-0 top-0">
            <ClockHelpButton />
          </div>
          <div className="absolute right-0 top-0">
            <LanguageSwitcher variant="ghost" size="sm" />
          </div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {ctx.company.name}
          </p>
          <h1 className="mt-1 text-2xl font-bold">
            {t("clock.hello", { name: ctx.profile.fullName ?? ctx.profile.email })}
          </h1>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <AddToHomeScreenButton />
            <EnablePushButton token={token} />
          </div>
        </div>

        <ClockMissingAlertsBanner token={token} />

        <Tabs defaultValue="clock" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="clock" className="gap-1 text-xs">
              <Clock className="h-3.5 w-3.5" /> {t("clock.tabs.clock")}
            </TabsTrigger>
            <TabsTrigger value="absence" className="gap-1 text-xs">
              <CalendarX className="h-3.5 w-3.5" /> {t("clock.tabs.absence")}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-1 text-xs">
              <History className="h-3.5 w-3.5" /> {t("clock.tabs.history")}
            </TabsTrigger>
          </TabsList>

          {/* Clock tab */}
          <TabsContent value="clock" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="h-5 w-5 text-primary" />
                  {active ? t("clock.openShift") : t("clock.clockIn")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {active ? (
                  <>
                    <div className="rounded-lg border border-border bg-muted/40 p-4">
                      <p className="text-sm text-muted-foreground">{t("clock.entry")}</p>
                      <p className="text-lg font-semibold">
                        {new Date(active.clock_in).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <Label>{t("clock.clockOutSiteLabel")}</Label>
                      <Select value={selOutJobSite} onValueChange={setSelOutJobSite}>
                        <SelectTrigger>
                          <SelectValue placeholder={t("clock.selectJobSite")} />
                        </SelectTrigger>
                        <SelectContent>
                          {ctx.jobSites.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                              {s.latitude == null || s.longitude == null
                                ? ` ${t("clock.withoutGps")}`
                                : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t("clock.clockOutSiteHint")}
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="break">{t("clock.break")}</Label>
                      <Input
                        id="break"
                        type="number"
                        min={0}
                        value={breakMin}
                        onChange={(e) => setBreakMin(Number(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes-out">{t("clock.notes")}</Label>
                      <Textarea
                        id="notes-out"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t("clock.optional")}
                        rows={2}
                      />
                    </div>
                    <Button
                      onClick={handleClockOut}
                      className="w-full gap-2"
                      size="lg"
                      disabled={busy}
                    >
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                      {busy ? t("clock.validatingGps") : t("clock.punchOut")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSelfieDialog({ open: true, kind: "out" })}
                      className="w-full gap-2"
                      size="lg"
                      disabled={busy}
                    >
                      <Camera className="h-4 w-4" />
                      {t("clock.punchOutSelfie")}
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">
                      {t("clock.selfieHint")}
                    </p>
                  </>
                ) : (
                  <>
                    <div>
                      <Label>{t("clock.jobSite")}</Label>
                      <Select value={selJobSite} onValueChange={setSelJobSite}>
                        <SelectTrigger>
                          <SelectValue placeholder={t("clock.selectJobSite")} />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredJobSites.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                              {s.latitude == null || s.longitude == null
                                ? ` ${t("clock.withoutGps")}`
                                : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(() => {
                        const site = ctx.jobSites.find((s) => s.id === selJobSite);
                        if (!site) return null;
                        const address = [
                          [site.address_line1, site.address_line2].filter(Boolean).join(", "),
                          [site.city, site.state].filter(Boolean).join(", "),
                          [site.postal_code, site.country].filter(Boolean).join(" "),
                        ]
                          .filter(Boolean)
                          .join(" • ");
                        if (!address) return null;
                        return (
                          <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
                            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                            <span>{address}</span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        <Trans
                          i18nKey="clock.gpsNotice"
                          values={{ meters: ctx.gpsRadiusMeters ?? GEOFENCE_RADIUS_METERS }}
                          components={{ strong: <strong /> }}
                        />
                      </span>
                    </div>
                    <Button
                      onClick={handleClockIn}
                      className="w-full gap-2"
                      size="lg"
                      disabled={busy}
                    >
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      {busy ? t("clock.validatingGps") : t("clock.punchIn")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!selJobSite) {
                          toast.error(t("clock.errors.selectJobSite"));
                          return;
                        }
                        setSelfieDialog({ open: true, kind: "in" });
                      }}
                      className="w-full gap-2"
                      size="lg"
                      disabled={busy}
                    >
                      <Camera className="h-4 w-4" />
                      {t("clock.punchInSelfie")}
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">
                      {t("clock.selfieHint")}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Absence tab */}
          <TabsContent value="absence" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarX className="h-5 w-5 text-primary" />
                  {t("clock.absence.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="abs-date">{t("clock.absence.date")}</Label>
                  <Input
                    id="abs-date"
                    type="date"
                    value={absDate}
                    max={todayStr}
                    onChange={(e) => setAbsDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>{t("clock.absence.category")}</Label>
                  <Select
                    value={absCategory}
                    onValueChange={(v) => setAbsCategory(v as typeof absCategory)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sick">{t("clock.absence.sick")}</SelectItem>
                      <SelectItem value="personal">{t("clock.absence.personal")}</SelectItem>
                      <SelectItem value="other">{t("clock.absence.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="abs-reason">{t("clock.absence.reason")}</Label>
                  <Textarea
                    id="abs-reason"
                    value={absReason}
                    onChange={(e) => setAbsReason(e.target.value)}
                    placeholder={t("clock.absence.reasonPlaceholder")}
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="abs-file" className="flex items-center gap-1">
                    <Paperclip className="h-3.5 w-3.5" />
                    {t("clock.absence.attachment")}
                  </Label>
                  <Input
                    id="abs-file"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setAbsFile(e.target.files?.[0] ?? null)}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("clock.absence.attachmentHint")}
                  </p>
                </div>
                <Button
                  onClick={handleSubmitAbsence}
                  className="w-full"
                  size="lg"
                  disabled={absSubmitting}
                >
                  {absSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("clock.absence.submit")}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History tab */}
          <TabsContent value="history" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <History className="h-5 w-5 text-primary" />
                  {t("clock.history.title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {historyLoading ? (
                  <div className="flex h-24 items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : absences.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {t("clock.history.empty")}
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {absences.map((a) => (
                      <li key={a.id} className="rounded-lg border border-border p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold">
                              {new Date(`${a.absence_date}T00:00:00`).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {a.category === "sick"
                                ? t("clock.history.categorySick")
                                : a.category === "personal"
                                  ? t("clock.history.categoryPersonal")
                                  : t("clock.history.categoryOther")}
                            </p>
                          </div>
                          <AbsenceStatusBadge status={a.status} />
                        </div>
                        <p className="mt-2 text-xs">{a.reason}</p>
                        {a.review_notes && (
                          <p className="mt-2 rounded bg-muted/50 p-2 text-xs text-muted-foreground">
                            <strong>{t("clock.history.answer")}</strong> {a.review_notes}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <p className="text-center text-xs text-muted-foreground">{t("clock.footerSecure")}</p>
      </div>

      <SelfieCaptureDialog
        open={selfieDialog.open}
        onOpenChange={(v) => setSelfieDialog((s) => ({ ...s, open: v }))}
        onCapture={handleSelfieCapture}
        busy={selfieBusy}
        title={selfieDialog.kind === "in" ? t("clock.selfieEntry") : t("clock.selfieExit")}
      />
    </div>
  );
}

function AbsenceStatusBadge({ status }: { status: "pending" | "approved" | "rejected" }) {
  const { t } = useTranslation();
  if (status === "approved") {
    return (
      <Badge variant="outline" className="border-primary/40 text-primary">
        {t("clock.history.approved")}
      </Badge>
    );
  }
  if (status === "rejected") {
    return (
      <Badge variant="outline" className="border-destructive/40 text-destructive">
        {t("clock.history.rejected")}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">
      {t("clock.history.pending")}
    </Badge>
  );
}
